import { useEffect, useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import { Event } from "@/pages/events/types";
import { CANCEL_EVENT_MUTATION } from "../../../queries";
import { useMutation } from "@apollo/client";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useParams } from "react-router-dom";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";
import { useCanceledEventEmailPreview } from "@/pages/events/_hooks/useCanceledEventEmailPreview";

export type CancelEvent = Pick<Event, "id" | "registrationCount" | "hasActiveOrders"> & { slug?: string };

interface CancelEventDialogProps {
  event: CancelEvent;
  open: boolean;
  setOpen: (value: boolean) => void;
  refetch?: () => void;
}

export default function CancelEventModal({ event, open, setOpen, refetch }: CancelEventDialogProps) {
  const { eventSlug: eventSlugParam = "" } = useParams();
  const eventSlug = event.slug || eventSlugParam;
  const { trackEvent } = useAmplitude();
  const hasRegistrations = (event.registrationCount ?? 0) > 0;
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useCanceledEventEmailPreview(event.id);

  useEffect(() => {
    if (!open) {
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview]);

  const [cancelEvent, { loading: mutationLoading }] = useMutation(CANCEL_EVENT_MUTATION, {
    onCompleted: (data) => {
      if (data.cancelEvent.success) {
        showSnackbar("Event cancelled", { close: true });
        refetch?.();
        setOpen(false);
        trackEvent(
          EVENT_ANALYTICS_EVENTS.canceled,
          getEventAnalyticsData({
            source: "cancel_event",
            eventId: event.id,
            eventSlug,
            action: "cancel_event",
            count: event.registrationCount
          })
        );
      } else {
        const message = data.cancelEvent.message;
        if (message) {
          showErrorSnackbar("Event not cancelled", { subtitle: message });
        } else {
          showErrorSnackbar("Event not cancelled", { subtitle: "Please try again later." });
        }
      }
    },
    onError: () => {
      showErrorSnackbar("Event not cancelled", { subtitle: "Please try again later." });
    }
  });

  const confirmationMessage = () => {
    const hasActiveOrders = !!event?.hasActiveOrders;

    if (hasRegistrations && !hasActiveOrders) {
      return "Are you sure you want to cancel this event? All registrants will be notified. This cannot be undone.";
    } else if (hasRegistrations && hasActiveOrders) {
      return "Are you sure you want to cancel this event? All registrants will be notified and their payments refunded. This cannot be undone.";
    } else {
      return "Are you sure you want to cancel this event? This cannot be undone.";
    }
  };

  async function handleCancel() {
    try {
      await cancelEvent({ variables: { id: event.id } });
    } catch {}
  }

  function handleClose() {
    setOpen(false);
  }

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview();
  };

  const dialogActions = (
    <EmailPreviewModalActions
      onPreview={handlePreviewEmail}
      previewDisabled={mutationLoading}
      showPreview={hasRegistrations}
    >
      <Button variant="text" onClick={handleClose} disabled={mutationLoading}>
        Go Back
      </Button>
      <Button variant="filled" onClick={handleCancel} testId="cancel-event-button" disabled={mutationLoading}>
        Cancel Event
      </Button>
    </EmailPreviewModalActions>
  );

  return (
    <>
      <Dialog
        open={open}
        onClosed={() => setOpen(false)}
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-w-[560px] p-6 pr-2`}
        headline="Cancel Event"
        hasFormElements={false}
        actions={dialogActions}
      >
        <p>{confirmationMessage()}</p>
      </Dialog>

      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={preview?.subject}
        html={preview?.html}
        loading={previewLoading}
        errorMessage={previewError?.message}
      />
    </>
  );
}
